import { useQuery } from "@apollo/client";
import type { ActivityHit, FacilityHit, PersonHit } from "@/components/AlgoliaSearch";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import {
  navigableGlobalSearchHits,
  toGlobalSearchActivityHit,
  toGlobalSearchFacilityHit,
  toGlobalSearchJobHit,
  toGlobalSearchPersonHit,
  type JobHit
} from "./mapGlobalSearchHits";
import {
  GLOBAL_SEARCH_ACTIVITIES_SEARCH_QUERY,
  GLOBAL_SEARCH_ENTITY_HITS_PER_PAGE,
  GLOBAL_SEARCH_FACILITIES_SEARCH_QUERY,
  GLOBAL_SEARCH_JOBS_SEARCH_QUERY,
  GLOBAL_SEARCH_PEOPLE_CAREER_SEARCH_QUERY,
  GLOBAL_SEARCH_PEOPLE_SEARCH_QUERY
} from "./queries";

const EMPTY_PEOPLE: PersonHit[] = [];
const EMPTY_FACILITIES: FacilityHit[] = [];
const EMPTY_JOBS: JobHit[] = [];
const EMPTY_ACTIVITIES: ActivityHit[] = [];
const EMPTY_RECENTLY_VIEWED_IDS: GlobalSearchRecentlyViewedIds = {
  people: [],
  facilities: [],
  jobs: [],
  activities: []
};

export type GlobalSearchRecentlyViewedIds = {
  people: readonly string[];
  facilities: readonly string[];
  jobs: readonly string[];
  activities: readonly string[];
};

export type GlobalSearchEntitySearch = {
  peopleHits: PersonHit[];
  facilityHits: FacilityHit[];
  jobHits: JobHit[];
  activityHits: ActivityHit[];
  pending: boolean;
  failed: boolean;
  peopleFailed: boolean;
  facilitiesFailed: boolean;
  jobsFailed: boolean;
  activitiesFailed: boolean;
};

function idleSearch(pending: boolean): GlobalSearchEntitySearch {
  return {
    peopleHits: EMPTY_PEOPLE,
    facilityHits: EMPTY_FACILITIES,
    jobHits: EMPTY_JOBS,
    activityHits: EMPTY_ACTIVITIES,
    pending,
    failed: false,
    peopleFailed: false,
    facilitiesFailed: false,
    jobsFailed: false,
    activitiesFailed: false
  };
}

export function useGlobalSearchEntitySearch(
  query: string,
  enabled: boolean,
  recentlyViewedIds: GlobalSearchRecentlyViewedIds = EMPTY_RECENTLY_VIEWED_IDS
): GlobalSearchEntitySearch {
  const trimmed = query.trim();
  const canViewPeople = useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPeople });
  const canViewPersonCareer = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonCareer
  });
  const canViewFacilities = useHasCapabilityBool({
    domain: DOMAIN.FACILITIES,
    permission: PERMISSION.viewFacilities
  });
  const canViewJobs = useHasCapabilityBool({ domain: DOMAIN.JOBS, permission: PERMISSION.viewJobPosts });
  const canViewActivities = useHasCapabilityBool({
    domain: DOMAIN.SECTIONS,
    permission: PERMISSION.manageEvents
  });

  const skip = !enabled || !trimmed;
  const skipPeople = skip || !canViewPeople;
  const skipFacilities = skip || !canViewFacilities;
  const skipJobs = skip || !canViewJobs;
  const skipActivities = skip || !canViewActivities;
  const peopleQuery = useQuery(GLOBAL_SEARCH_PEOPLE_SEARCH_QUERY, {
    variables: {
      search: trimmed,
      first: GLOBAL_SEARCH_ENTITY_HITS_PER_PAGE,
      recentlyViewedIds: recentlyViewedIds.people as string[]
    },
    skip: skipPeople,
    fetchPolicy: "no-cache",
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true
  });
  const peopleCareerQuery = useQuery(GLOBAL_SEARCH_PEOPLE_CAREER_SEARCH_QUERY, {
    variables: {
      search: trimmed,
      first: GLOBAL_SEARCH_ENTITY_HITS_PER_PAGE,
      recentlyViewedIds: recentlyViewedIds.people as string[]
    },
    skip: skipPeople || !canViewPersonCareer,
    fetchPolicy: "no-cache",
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true
  });
  const facilitiesQuery = useQuery(GLOBAL_SEARCH_FACILITIES_SEARCH_QUERY, {
    variables: {
      search: trimmed,
      first: GLOBAL_SEARCH_ENTITY_HITS_PER_PAGE,
      recentlyViewedIds: recentlyViewedIds.facilities as string[]
    },
    skip: skipFacilities,
    fetchPolicy: "network-only",
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true
  });
  const jobsQuery = useQuery(GLOBAL_SEARCH_JOBS_SEARCH_QUERY, {
    variables: {
      searchTerm: trimmed,
      first: GLOBAL_SEARCH_ENTITY_HITS_PER_PAGE,
      recentlyViewedIds: recentlyViewedIds.jobs as string[]
    },
    skip: skipJobs,
    fetchPolicy: "network-only",
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true
  });
  const activitiesQuery = useQuery(GLOBAL_SEARCH_ACTIVITIES_SEARCH_QUERY, {
    variables: {
      search: trimmed,
      first: GLOBAL_SEARCH_ENTITY_HITS_PER_PAGE,
      recentlyViewedIds: recentlyViewedIds.activities as string[]
    },
    skip: skipActivities,
    fetchPolicy: "network-only",
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true
  });

  const queryReady = enabled && Boolean(trimmed);
  const inFlight =
    (!skipPeople && (peopleQuery.loading || (canViewPersonCareer && peopleCareerQuery.loading))) ||
    (!skipFacilities && facilitiesQuery.loading) ||
    (!skipJobs && jobsQuery.loading) ||
    (!skipActivities && activitiesQuery.loading);
  const pending = queryReady && inFlight;

  if (!queryReady || inFlight) {
    return idleSearch(pending);
  }

  const peopleFailed = !skipPeople && Boolean(peopleQuery.error);
  const facilitiesFailed = !skipFacilities && Boolean(facilitiesQuery.error);
  const jobsFailed = !skipJobs && Boolean(jobsQuery.error);
  const activitiesFailed = !skipActivities && Boolean(activitiesQuery.error);

  const mergedPeopleNodes = (() => {
    const baseNodes = peopleQuery.data?.fuzzySearchPeople?.nodes ?? [];
    if (skipPeople || peopleFailed || !canViewPersonCareer) return baseNodes;

    const careerByKey = new Map(
      (peopleCareerQuery.data?.fuzzySearchPeople?.nodes ?? []).map((node) => [node.pgaId ?? node.id, node])
    );

    return baseNodes.map((node) => {
      const careerNode = careerByKey.get(node.pgaId ?? node.id);
      if (!careerNode) return node;
      return {
        ...node,
        primaryFacility: careerNode.primaryFacility,
        employments: careerNode.employments
      };
    });
  })();

  const peopleHits =
    skipPeople || peopleFailed
      ? EMPTY_PEOPLE
      : navigableGlobalSearchHits(mergedPeopleNodes.map(toGlobalSearchPersonHit));
  const facilityHits =
    skipFacilities || facilitiesFailed
      ? EMPTY_FACILITIES
      : navigableGlobalSearchHits(
          (facilitiesQuery.data?.fuzzySearchFacilities?.nodes ?? []).map(toGlobalSearchFacilityHit)
        );
  const jobHits =
    skipJobs || jobsFailed
      ? EMPTY_JOBS
      : navigableGlobalSearchHits((jobsQuery.data?.fuzzySearchJobs?.nodes ?? []).map(toGlobalSearchJobHit));
  const activityHits =
    skipActivities || activitiesFailed
      ? EMPTY_ACTIVITIES
      : navigableGlobalSearchHits(
          (activitiesQuery.data?.fuzzySearchActivities?.nodes ?? []).map(toGlobalSearchActivityHit)
        );

  return {
    peopleHits,
    facilityHits,
    jobHits,
    activityHits,
    pending: false,
    failed:
      (peopleFailed || facilitiesFailed || jobsFailed || activitiesFailed) &&
      peopleHits.length === 0 &&
      facilityHits.length === 0 &&
      jobHits.length === 0 &&
      activityHits.length === 0,
    peopleFailed,
    facilitiesFailed,
    jobsFailed,
    activitiesFailed
  };
}
